import { useMemo, useState } from "react";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import ErrorMessages from "../../../components/ErrorMessages/ErrorMessage";
import { WarningIcon } from "../../../components/Icons/WarningIcon";
import { InfoRow } from "../../../components/InfoRow/InfoRow";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import type { TaskProps } from "../../../types/TaskProps";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import { getAccountNumberCommonProcess } from "../cross-data-check";
import type { ComponentConfig } from "../types";
import { DebtCard } from "./DebtCard";
import { getAccountNumberError, getPaymentReferenceError } from "./helpers";
import {
  CaseInfoContainer,
  DebtList,
  DebtPanel,
  EmptyText,
  FilterBar,
  FilterPill,
  PaddingContainer,
  PanelHeader,
  PanelTitle,
  SectionTitle,
  WarningContainerRect,
  WarningText
} from "./style";
import type {
  DebtFilter,
  EditableDebtField,
  RefinanceDebt,
  VerifyRefinanceContext
} from "./types";
import { useFormAnchoredHeight } from "./useFormAnchoredHeight";

const VerifyRefinance = (props: TaskProps) => {
  const context: VerifyRefinanceContext = props?.task?.context ?? {
    refinanceDebts: []
  };

  const { refinanceDebts } = context;

  // The applicant banner and case details read straight from the flow data (as
  // cross-data-check does), so the task context stays limited to the debt fields.
  const applicant = props?.flow?.data?.agentApiApplication?.applicant;
  const appliedLoanAmount = props?.flow?.data?.offer?.finalLoanAmount ?? "-";
  const accountNumber = getAccountNumberCommonProcess(props.flow) || "-";
  const email = applicant?.emailAddress ?? "-";
  const phone = applicant?.mobileNumber ?? "-";

  const loadedDecision =
    typeof props?.task?.data?.decision === "boolean"
      ? props.task.data.decision
      : null;
  const loadedComment = props?.task?.data?.comment ?? "";

  const [decision, setDecision] = useState<boolean | null>(loadedDecision);
  const [comment, setComment] = useState<string>(loadedComment);
  const [errorMessages, setErrorMessages] = useState<string>("");

  // Editable copy of the debts, kept in the original order so it maps back to the stored
  // payouts by position. Start from the context (which carries `valid`/`isFromUc`) and
  // overlay any previously saved edits by index — the saved payload only holds the
  // editable fields, so replacing the context outright would drop `valid`/`isFromUc`.
  const [debts, setDebts] = useState<RefinanceDebt[]>(() => {
    const saved: Partial<RefinanceDebt>[] | undefined =
      props?.task?.data?.debts;
    if (!saved) {
      return refinanceDebts;
    }
    return refinanceDebts.map((debt, index) =>
      saved[index] ? { ...debt, ...saved[index] } : debt
    );
  });

  const [filter, setFilter] = useState<DebtFilter>("needs-review");

  // Expand the debts that need review by default; the caseworker can toggle any card.
  const [expanded, setExpanded] = useState<Set<number>>(
    () =>
      new Set(
        debts.reduce<number[]>((acc, debt, index) => {
          if (!debt.valid) {
            acc.push(index);
          }
          return acc;
        }, [])
      )
  );

  // Cap the left column to the right form column's height so the right side is the static
  // anchor (buttons stay put) and the debt list scrolls once it grows taller than the form.
  // Re-measure when the error message toggles, since it changes the form column's height.
  const {
    formProbeRef,
    panelRef: debtPanelRef,
    maxHeight: debtMaxHeight
  } = useFormAnchoredHeight([errorMessages]);

  const updateDebt = (index: number, key: EditableDebtField, value: string) => {
    setDebts((prev) =>
      prev.map((debt, i) => {
        if (i !== index) {
          return debt;
        }
        const updated = { ...debt, [key]: value };
        // `valid` reflects whether the number was on Risk's verified list at load time. Once
        // the caseworker edits the bankgiro it is no longer that verified number, so it must
        // stop counting as verified (badge, filters and the warning banner). It only counts as
        // verified again if edited back to the original value that loaded as verified.
        if (key === "bankgiroNumber") {
          const original = refinanceDebts[index];
          updated.valid =
            original?.valid === true && value === original.bankgiroNumber;
        }
        return updated;
      })
    );
  };

  const toggleExpanded = (index: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const verifiedCount = debts.filter((debt) => debt.valid).length;
  const needsReviewCount = debts.length - verifiedCount;

  // Render unverified debts first, then apply the active filter. Keep the original index so
  // edits and the position-based save still line up.
  const visibleDebts = useMemo(() => {
    const ordered = debts
      .map((debt, index) => ({ debt, index }))
      .sort((a, b) => Number(a.debt.valid) - Number(b.debt.valid));
    if (filter === "needs-review") {
      return ordered.filter(({ debt }) => !debt.valid);
    }
    if (filter === "verified") {
      return ordered.filter(({ debt }) => debt.valid);
    }
    return ordered;
  }, [debts, filter]);

  // Indexes of debts the caseworker still has to fix before approving — an invalid bankgiro, or
  // an invalid payment reference (OCR). Both validators match the backend, so what the portal
  // blocks is exactly what the backend would reject.
  const invalidDebtIndexes = useMemo(
    () =>
      debts.reduce<number[]>((acc, debt, index) => {
        if (
          getAccountNumberError(debt.bankgiroNumber) ||
          getPaymentReferenceError(debt.paymentReference ?? "")
        ) {
          acc.push(index);
        }
        return acc;
      }, []),
    [debts]
  );

  const hasInvalidDebt = invalidDebtIndexes.length > 0;

  const buildFormData = () => ({
    decision,
    comment,
    debts: debts.map((debt) => ({
      type: debt.type,
      bankgiroNumber: debt.bankgiroNumber,
      bankName: debt.bankName,
      amount: debt.amount,
      paymentReference: debt.paymentReference
    }))
  });

  const isReject = decision === false;

  const onComplete = () => {
    if (decision === null) {
      setErrorMessages("Please select a decision");
      return;
    }
    // Rejecting only needs a comment; approving requires all bankgiro numbers to be valid.
    if (isReject && !comment.trim()) {
      setErrorMessages("Please add a comment explaining the rejection");
      return;
    }
    if (!isReject && hasInvalidDebt) {
      // Reveal every debt that needs fixing so the blocking card can't stay hidden behind the
      // "Needs review" filter or collapsed: switch to "All" and expand the offending cards.
      setFilter("all");
      setExpanded((prev) => {
        const next = new Set(prev);
        for (const index of invalidDebtIndexes) {
          next.add(index);
        }
        return next;
      });
      setErrorMessages(
        "One or more debts have an invalid bankgiro number or payment reference. The affected debts are highlighted below."
      );
      return;
    }
    props.complete(
      buildFormData(),
      () => {},
      (e: any) => {
        setErrorMessages(e?.detail ?? "Something went wrong");
      }
    );
  };

  const onSave = () => {
    props.save(
      buildFormData(),
      () => {},
      (e: any) => {
        setErrorMessages(e?.detail ?? "Something went wrong");
      }
    );
  };

  const hasUnverified = needsReviewCount > 0;

  const filters: { key: DebtFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: debts.length },
    { key: "needs-review", label: "Needs review", count: needsReviewCount },
    { key: "verified", label: "Verified", count: verifiedCount }
  ];

  const formConfig: ComponentConfig[] = [
    {
      component: (
        <div ref={formProbeRef}>
          <ApplicantInfo applicant={applicant} />
        </div>
      ),
      order: 0
    },
    {
      component: (
        <PaddingContainer>
          <WarningContainerRect>
            <WarningIcon />
            <WarningText>
              <strong>
                {`${needsReviewCount} of ${debts.length} bankgiro numbers`}
              </strong>{" "}
              weren't found in the bankgiro register. Fix or confirm each one
              before approving.
            </WarningText>
          </WarningContainerRect>
        </PaddingContainer>
      ),
      order: 1,
      hide: !hasUnverified
    },
    {
      component: (
        <PaddingContainer>
          <SectionTitle>Case Details</SectionTitle>
          <CaseInfoContainer>
            <InfoRow
              title="Applied loan amount"
              value={appliedLoanAmount}
              hasMatch={false}
              hasPadding={false}
            />
            <InfoRow
              title="Account number"
              value={accountNumber}
              hasMatch={false}
              hasPadding={false}
            />
            <InfoRow
              title="Email address"
              value={email}
              hasMatch={false}
              hasPadding={false}
            />
            <InfoRow
              title="Phone number"
              value={phone}
              hasMatch={false}
              hasPadding={false}
            />
          </CaseInfoContainer>
        </PaddingContainer>
      ),
      order: 2
    },
    {
      component: (
        <RadioButtonGroup
          radioButtonValue={decision}
          onChange={(e: string) =>
            e === "true" ? setDecision(true) : setDecision(false)
          }
          title="Decision"
          options={[
            { value: true, label: "Approve bankgiro numbers" },
            { value: false, label: "Reject application" }
          ]}
          id="verify-refinance"
        />
      ),
      order: 3
    },
    {
      component: (
        <FormTextArea
          name="Comment"
          onChange={(e: string) => setComment(e)}
          label="Comment"
          defaultValue={comment || ""}
          placeholder="Add a comment about the bankgiro verification"
        />
      ),
      order: 4
    },
    {
      component: <ErrorMessages errors={[errorMessages]} />,
      order: 5,
      hide: errorMessages === ""
    }
  ];

  const infoConfig: ComponentConfig[] = [
    {
      component: (
        <DebtPanel ref={debtPanelRef} style={{ maxHeight: debtMaxHeight }}>
          <PanelHeader>
            <PanelTitle>Debts to refinance</PanelTitle>
            <FilterBar>
              {filters.map((f) => (
                <FilterPill
                  key={f.key}
                  type="button"
                  $active={filter === f.key}
                  onClick={() => setFilter(f.key)}
                >
                  {`${f.label} ${f.count}`}
                </FilterPill>
              ))}
            </FilterBar>
          </PanelHeader>
          <DebtList>
            {visibleDebts.length > 0 ? (
              visibleDebts.map(({ debt, index }) => (
                <DebtCard
                  key={index}
                  debt={debt}
                  index={index}
                  expanded={expanded.has(index)}
                  onToggle={() => toggleExpanded(index)}
                  onChange={(key, value) => updateDebt(index, key, value)}
                />
              ))
            ) : (
              <EmptyText>No debts in this view.</EmptyText>
            )}
          </DebtList>
        </DebtPanel>
      ),
      order: 1
    }
  ];

  return (
    <CommonUserTaskNew
      handleComplete={onComplete}
      handleSave={onSave}
      infoConfig={infoConfig}
      formConfig={formConfig}
      overrideMasonryColumns={1}
    />
  );
};

export default VerifyRefinance;
